import { useState } from 'react';
import { CheckCircle2, Pencil, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import { errorMessage } from '../../../lib/errorMessage';
import { InvoiceDrawer } from '../../finance/InvoiceDrawer';
import { PaymentDrawer, type PaymentPreset } from '../../finance/PaymentDrawer';
import {
  formatILS,
  INVOICE_STATUS_LABELS_HE,
  PAYMENT_METHOD_LABELS_HE,
  summarizeFinance,
  type ProjectInvoice,
  type ProjectPayment,
} from '../../finance/projectFinance';
import {
  useDeleteInvoice,
  useDeletePayment,
  useProjectFinance,
} from '../../finance/projectFinanceQueries';
import type { ProjectRow } from './projects.schema';
import styles from './projects.module.css';

function formatDay(iso: string | null): string {
  return iso ? format(new Date(iso), 'dd/MM/yyyy', { locale: he }) : '—';
}

interface Props {
  project: ProjectRow;
  orgId: string;
  userId: string | null;
  onEditProject: () => void;
}

export function ProjectFinanceTab({ project, orgId, userId, onEditProject }: Props) {
  const toast = useToast();
  const { data, isLoading, error } = useProjectFinance(orgId, project.id);
  const invoices = data?.invoices ?? [];
  const payments = data?.payments ?? [];
  const summary = summarizeFinance(project.value_amount, invoices, payments);
  const deleteInvoice = useDeleteInvoice(orgId);
  const deletePayment = useDeletePayment(orgId);

  const [invoiceDrawer, setInvoiceDrawer] = useState<{
    open: boolean;
    invoice: ProjectInvoice | null;
  }>({
    open: false,
    invoice: null,
  });
  const [paymentDrawer, setPaymentDrawer] = useState<{
    open: boolean;
    payment: ProjectPayment | null;
    preset: PaymentPreset | null;
  }>({ open: false, payment: null, preset: null });

  async function removeInvoice(invoice: ProjectInvoice) {
    const attached = payments.filter((p) => p.invoice_id === invoice.id).length;
    const warn = attached > 0 ? ` ${String(attached)} התשלומים שלה יישארו, בלי שיוך לחשבונית.` : '';
    if (!window.confirm(`למחוק את חשבונית ${invoice.invoice_number}?${warn}`)) return;
    try {
      await deleteInvoice.mutateAsync(invoice.id);
      toast.success('החשבונית נמחקה');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  async function removePayment(payment: ProjectPayment) {
    if (!window.confirm(`למחוק תשלום של ${formatILS(payment.amount)}?`)) return;
    try {
      await deletePayment.mutateAsync(payment.id);
      toast.success('התשלום נמחק');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  const invoiceNumberById = Object.fromEntries(invoices.map((i) => [i.id, i.invoice_number]));

  return (
    <div className={styles.overview}>
      <div className={styles.overviewGrid}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>ערך הפרויקט</span>
          <span className={styles.kpiValue}>
            <bdi>{formatILS(summary.value)}</bdi>
          </span>
          {project.value_amount === 0 ? (
            <button
              type="button"
              className={cn(styles.btnSecondary, styles.btnSmall)}
              onClick={onEditProject}
            >
              קבע ערך
            </button>
          ) : null}
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>חושבן</span>
          <span className={styles.kpiValue}>
            <bdi>{formatILS(summary.invoiced)}</bdi>
          </span>
          <span className={styles.kpiSub}>
            נשאר לחשבון <bdi>{formatILS(summary.toInvoice)}</bdi>
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>התקבל</span>
          <span className={cn(styles.kpiValue, styles.kpiGood)}>
            <bdi>{formatILS(summary.received)}</bdi>
          </span>
          <span className={styles.kpiSub}>
            נשאר לקבל <bdi>{formatILS(summary.outstanding)}</bdi>
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>לגבייה</span>
          <span className={cn(styles.kpiValue, summary.toCollect > 0 && styles.kpiWarn)}>
            <bdi>{formatILS(summary.toCollect)}</bdi>
          </span>
          <span className={styles.kpiSub}>חשבוניות שהוצאו ועוד לא שולמו</span>
        </div>
      </div>

      {error ? (
        <p className={styles.notice}>לא הצלחנו לטעון כספים · {errorMessage(error)}</p>
      ) : null}
      {isLoading ? <p className={styles.notice}>טוען...</p> : null}

      <div className={styles.sectionCard}>
        <div className={styles.sectionCardHead}>
          <h3>חשבוניות</h3>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              setInvoiceDrawer({ open: true, invoice: null });
            }}
          >
            <Plus size={14} aria-hidden="true" />
            הוסף חשבונית
          </button>
        </div>
        {invoices.length === 0 ? (
          <p className={styles.notice}>אין חשבוניות לפרויקט.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>מספר</th>
                  <th>תאריך</th>
                  <th>סכום</th>
                  <th>שולם</th>
                  <th>יתרה</th>
                  <th>סטטוס</th>
                  <th aria-label="פעולות" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const bal = summary.byInvoice[inv.id] ?? { paid: 0, due: 0 };
                  return (
                    <tr key={inv.id}>
                      <td>
                        <bdi>{inv.invoice_number}</bdi>
                      </td>
                      <td>
                        <bdi>{formatDay(inv.sent_at)}</bdi>
                      </td>
                      <td>
                        <bdi>{formatILS(inv.amount)}</bdi>
                      </td>
                      <td>
                        <bdi>{formatILS(bal.paid)}</bdi>
                      </td>
                      <td>
                        <bdi>{formatILS(bal.due)}</bdi>
                      </td>
                      <td>
                        <span className={cn(styles.statusBadge, styles[`invoice--${inv.status}`])}>
                          {INVOICE_STATUS_LABELS_HE[inv.status]}
                        </span>
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          {bal.due > 0 ? (
                            <button
                              type="button"
                              className={styles.iconBtn}
                              aria-label={`רשום תשלום על חשבונית ${inv.invoice_number}`}
                              title="רשום תשלום על היתרה"
                              onClick={() => {
                                setPaymentDrawer({
                                  open: true,
                                  payment: null,
                                  preset: { invoice_id: inv.id, amount: bal.due },
                                });
                              }}
                            >
                              <CheckCircle2 size={13} aria-hidden="true" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label={`ערוך חשבונית ${inv.invoice_number}`}
                            onClick={() => {
                              setInvoiceDrawer({ open: true, invoice: inv });
                            }}
                          >
                            <Pencil size={13} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label={`מחק חשבונית ${inv.invoice_number}`}
                            disabled={deleteInvoice.isPending}
                            onClick={() => {
                              void removeInvoice(inv);
                            }}
                          >
                            <Trash2 size={13} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.sectionCardHead}>
          <h3>תשלומים</h3>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              setPaymentDrawer({ open: true, payment: null, preset: null });
            }}
          >
            <Plus size={14} aria-hidden="true" />
            רשום תשלום
          </button>
        </div>
        {payments.length === 0 ? (
          <p className={styles.notice}>לא נרשמו תשלומים. מקדמה נרשמת כאן גם בלי חשבונית.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>סכום</th>
                  <th>אמצעי</th>
                  <th>חשבונית</th>
                  <th>הערות</th>
                  <th aria-label="פעולות" />
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <bdi>{formatDay(p.paid_at)}</bdi>
                    </td>
                    <td>
                      <bdi>{formatILS(p.amount)}</bdi>
                    </td>
                    <td>{PAYMENT_METHOD_LABELS_HE[p.method]}</td>
                    <td>
                      {p.invoice_id ? <bdi>{invoiceNumberById[p.invoice_id] ?? '—'}</bdi> : 'מקדמה'}
                    </td>
                    <td className={styles.wrap}>{p.notes ?? '—'}</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          aria-label="ערוך תשלום"
                          onClick={() => {
                            setPaymentDrawer({ open: true, payment: p, preset: null });
                          }}
                        >
                          <Pencil size={13} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          aria-label="מחק תשלום"
                          disabled={deletePayment.isPending}
                          onClick={() => {
                            void removePayment(p);
                          }}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InvoiceDrawer
        open={invoiceDrawer.open}
        invoice={invoiceDrawer.invoice}
        projectId={project.id}
        suggestedAmount={summary.toInvoice}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setInvoiceDrawer({ open: false, invoice: null });
        }}
      />
      <PaymentDrawer
        open={paymentDrawer.open}
        payment={paymentDrawer.payment}
        preset={paymentDrawer.preset}
        projectId={project.id}
        invoices={invoices}
        balances={summary.byInvoice}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setPaymentDrawer({ open: false, payment: null, preset: null });
        }}
      />
    </div>
  );
}
